import React, { useState, useRef, useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import {
  FaRegLightbulb,
  FaInfoCircle,
  FaSpinner,
  FaTimes,
  FaPaperPlane,
  FaRobot,
  FaUser,
  FaCommentDots,
  FaChevronDown,
  FaChevronUp,
  FaTrashAlt,
  FaExpand,
  FaCompress,
  FaBullhorn,
  FaCheck,
} from "react-icons/fa";
import { HiSparkles } from "react-icons/hi2";
import { createDraftNudgeFromAI } from "../../../helper/customizedNudge";
import ArtifactCard from "./ArtifactCard";
import {
  fetchRecommendations,
  selectRecommendations,
  selectDataSummary,
  selectDataGaps,
  setProgress,
  clearProgress,
  selectProgressStep,
  selectProgressMessage,
  clearChatHistory,
  selectRequestInProgress,
  setRequestInProgress,
  setDrawerOpen,
  selectStudioArtifact,
} from "../agentSlice";
import { selectLogInUsername } from "../../../common/authenticateDashboard/authSlice";
import RecommendationCard from "./RecommendationCard";
import { agentAPI, consumeSSEStream } from "../agentAPI";
import ProgressSteps from "./ProgressSteps";
import ChatMarkdown from "./ChatMarkdown";
import {
  NUDGE_QUICK_QUESTION,
  ABTEST_QUICK_QUESTION,
  LANDING_QUICK_QUESTION,
  IMPROVE_QUICK_QUESTION,
  isAbTestStudioRequest,
  isLandingBuilderRequest,
  isNudgeRequest,
  isImproveConversionRequest,
} from "../intents";

const safeRender = (val) => {
  if (typeof val === "string" || typeof val === "number") return val;
  if (typeof val === "object" && val !== null) return val.title || val.text || val.recommendation || JSON.stringify(val);
  return "";
};

const AIDrawer = ({ isOpen, onClose, asPage = false }) => {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const sessionUser = useSelector(selectLogInUsername);
  const clientId = sessionUser ? sessionUser.split("@")[0] : null;

  const recommendations = useSelector(selectRecommendations);
  const dataSummary = useSelector(selectDataSummary);
  const dataGaps = useSelector(selectDataGaps);
  const loading = useSelector((state) => state.agent.loadingRecommendations);
  const error = useSelector((state) => state.agent.errorRecommendations);
  const requestInProgress = useSelector(selectRequestInProgress);

  const progressStep = useSelector(selectProgressStep);
  const progressMessage = useSelector(selectProgressMessage);

  const [activeTab, setActiveTab] = useState("insights");
  const [isSummaryExpanded, setIsSummaryExpanded] = useState(false);
  const [queueText, setQueueText] = useState("");

  // Ask AI local state
  const [chatMessages, setChatMessages] = useState([]);
  const [inputVal, setInputVal] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const [hasContext, setHasContext] = useState(false);
  const [draftingId, setDraftingId] = useState(null); // message id currently creating a draft
  const studioArtifact = useSelector(selectStudioArtifact);
  const addedArtifactRef = useRef(null);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // When a UI artifact arrives from the studio (e.g. after Minimize), drop the
  // WHOLE studio conversation into the chat, followed by the code file card.
  useEffect(() => {
    const art = studioArtifact;
    if (art?.html && art.artifactId && addedArtifactRef.current !== art.artifactId) {
      addedArtifactRef.current = art.artifactId;
      const ts = new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
      const convo = (Array.isArray(art.conversation) ? art.conversation : []).map((m, i) => ({
        id: `${art.artifactId}_m${i}`,
        sender: m.sender,
        text: m.text,
        candidates: m.candidates, // rendered read-only in the drawer
        timestamp: ts,
      }));
      setChatMessages((prev) => [
        ...prev,
        ...convo,
        { id: art.artifactId, sender: "agent", artifactCard: art, timestamp: ts },
      ]);
      setActiveTab("ask");
    }
  }, [studioArtifact]);

  // Fetch recommendations with SSE
  const loadRecommendations = useCallback(async () => {
    if (!clientId) return;
    dispatch(setRequestInProgress(true));
    dispatch(setProgress({ step: 0, message: "Starting analysis..." }));

    try {
      const streamUrl = agentAPI.streamRecommendations(clientId);
      await consumeSSEStream(streamUrl, {
        method: "GET",
        onProgress: (step, message) => {
          dispatch(setProgress({ step, message }));
        },
        onResult: (data) => {
          dispatch(clearProgress());
          dispatch({
            type: "agent/fetchRecommendations/fulfilled",
            payload: data,
          });
          dispatch(setRequestInProgress(false));
        },
        onError: (error) => {
          dispatch(clearProgress());
          console.error("Stream error:", error);
        },
      });
    } catch (err) {
      dispatch(clearProgress());
      dispatch(fetchRecommendations(clientId));
    }
  }, [clientId, dispatch]);

  useEffect(() => {
    if (isOpen && clientId) {
      // If we already have recommendations, show them immediately
      if (recommendations.length > 0) {
        // Show existing recommendations, don't refetch
        return;
      }

      // Only fetch if we have no recommendations and no request is in progress
      if (!requestInProgress) {
        dispatch({ type: "agent/fetchRecommendations/pending" });
        loadRecommendations();
      }
    }
  }, [isOpen, clientId, loadRecommendations, dispatch, recommendations, requestInProgress]);

  // Queue position polling
  useEffect(() => {
    let intervalId;
    if (isTyping && clientId) {
      const fetchQueueStatus = async () => {
        try {
          const res = await agentAPI.getQueueStatus();
          const status = res.data;
          if (status) {
            const queued = status.queued || [];
            const active = status.active;
            
            // Check if we are active or queued
            if (active && active.client === clientId) {
              setQueueText("Processing your request...");
            } else {
              const myIndex = queued.findIndex(q => q.client === clientId);
              if (myIndex >= 0) {
                const ahead = myIndex;
                if (ahead === 0) {
                  setQueueText("Your question is next in line — processing soon");
                } else {
                  setQueueText(`Your question is in queue — ${ahead} request${ahead > 1 ? "s" : ""} ahead of you`);
                }
              } else {
                if (queued.length > 0) {
                  setQueueText(`Queue is busy — ${queued.length} request${queued.length > 1 ? "s" : ""} in line`);
                } else {
                  setQueueText("");
                }
              }
            }
          }
        } catch (err) {
          console.error("Error fetching queue status:", err);
        }
      };
      
      fetchQueueStatus();
      intervalId = setInterval(fetchQueueStatus, 5000);
    } else {
      setQueueText("");
    }
    
    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [isTyping, clientId]);

  // Lock body scroll when open & toggle active class (drawer/overlay mode only)
  useEffect(() => {
    if (asPage) return undefined;
    if (isOpen) {
      document.body.style.overflow = "hidden";
      document.body.classList.add("ai-agent-active");
    } else {
      document.body.style.overflow = "";
      document.body.classList.remove("ai-agent-active");
    }
    return () => {
      document.body.style.overflow = "";
      document.body.classList.remove("ai-agent-active");
    };
  }, [isOpen, asPage]);

  // Escape key to close (drawer/overlay mode only)
  useEffect(() => {
    if (asPage) return undefined;
    const handleEsc = (e) => {
      if (e.key === "Escape" && isOpen) onClose();
    };
    document.addEventListener("keydown", handleEsc);
    return () => document.removeEventListener("keydown", handleEsc);
  }, [isOpen, onClose, asPage]);

  // Auto-scroll chat
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [chatMessages, isTyping]);

  // Focus input when switching to Ask AI tab
  useEffect(() => {
    if (activeTab === "ask" && isOpen && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 300);
    }
  }, [activeTab, isOpen]);

  // Route an "improve conversion" intent to the 3-action hub.
  const handleImprove = async (queryText) => {
    const userMsg = { id: `user_${Date.now()}`, sender: "user", text: queryText, timestamp: new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) };
    const agentMsgId = `agent_${Date.now()}`;
    setChatMessages((prev) => [...prev, userMsg, { id: agentMsgId, sender: "agent", text: "", isStreaming: true, progressStep: 0, progressMessage: "Finding the best ways to lift conversion..." }]);
    setIsTyping(true);
    try {
      const res = await agentAPI.improveConversion(clientId);
      setChatMessages((prev) => prev.map((m) => m.id === agentMsgId ? { ...m, text: "Here are three data-backed ways to improve your conversion. Pick one to get started:", improveActions: res.data?.plan || {}, isStreaming: false } : m));
    } catch (err) {
      setChatMessages((prev) => prev.map((m) => m.id === agentMsgId ? { ...m, text: `Couldn't load suggestions. ${err?.response?.data?.error || err.message || ""}`, isStreaming: false, isError: true } : m));
    } finally {
      setIsTyping(false);
    }
  };

  // Launch the chosen improvement action.
  const handleImproveAction = (action) => {
    if (action === "nudge") { handleNudgeAdvisor(NUDGE_QUICK_QUESTION); return; }
    if (onClose) onClose();
    if (action === "abtest") navigate("/ab-test-studio", { state: { mode: "abtest", initialRequest: "Create an A/B test to improve conversion" } });
    else if (action === "landing") navigate("/ab-test-studio", { state: { mode: "landing", initialRequest: "Redesign my landing page to improve conversion" } });
  };

  // Ask the backend for a nudge strategy from heatmap + journey insights, then
  // render the explanation plus a draft card the user can create & review.
  const handleNudgeAdvisor = async (queryText) => {
    const userMsg = {
      id: `user_${Date.now()}`,
      sender: "user",
      text: queryText,
      timestamp: new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
    };
    const agentMsgId = `agent_${Date.now()}`;
    const agentMsg = {
      id: agentMsgId,
      sender: "agent",
      text: "",
      isStreaming: true,
      progressStep: 0,
      progressMessage: "Analyzing heatmap & user journey...",
      timestamp: new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
    };
    setChatMessages((prev) => [...prev, userMsg, agentMsg]);
    setIsTyping(true);

    try {
      const res = await agentAPI.nudgeAdvisor(clientId);
      const data = res.data || {};
      const spec = data.nudgeSpec || null;
      const explanation = data.explanation
        || "I analyzed your heatmap and user journey but couldn't form a confident nudge recommendation yet.";
      setChatMessages((prev) =>
        prev.map((m) =>
          m.id === agentMsgId
            ? { ...m, text: explanation, nudgeSpec: spec, isStreaming: false }
            : m
        )
      );
    } catch (err) {
      setChatMessages((prev) =>
        prev.map((m) =>
          m.id === agentMsgId
            ? { ...m, text: `Couldn't generate a nudge suggestion. ${err?.response?.data?.error || err.message || ""}`, isStreaming: false }
            : m
        )
      );
    } finally {
      setIsTyping(false);
    }
  };

  // Build a DRAFT nudge (status OFF) from the AI spec, then open the editor.
  const handleCreateDraft = async (msgId, spec) => {
    if (!spec || draftingId) return;
    setDraftingId(msgId);
    try {
      await createDraftNudgeFromAI(clientId, spec);
      setChatMessages((prev) =>
        prev.map((m) =>
          m.id === msgId
            ? { ...m, draftCreated: true, text: "Draft nudge created (currently OFF). Please review it and toggle it ON to publish." }
            : m
        )
      );
      if (onClose) onClose();
      navigate("/custom-nudge");
    } catch (err) {
      setChatMessages((prev) =>
        prev.map((m) =>
          m.id === msgId ? { ...m, text: `Couldn't create the draft nudge. ${err.message || ""}` } : m
        )
      );
    } finally {
      setDraftingId(null);
    }
  };

  const handleSend = useCallback(
    async (e, customText = null) => {
      if (e) e.preventDefault();
      const queryText = (customText || inputVal).trim();
      if (!queryText || !clientId || isTyping) return;

      setInputVal("");

      // Intent routing — tools first (most specific), then the improve hub.
      if (queryText === ABTEST_QUICK_QUESTION || isAbTestStudioRequest(queryText)) {
        if (onClose) onClose();
        navigate("/ab-test-studio", { state: { initialRequest: queryText, mode: "abtest" } });
        return;
      }
      if (queryText === LANDING_QUICK_QUESTION || isLandingBuilderRequest(queryText)) {
        if (onClose) onClose();
        navigate("/ab-test-studio", { state: { initialRequest: queryText, mode: "landing" } });
        return;
      }
      if (isNudgeRequest(queryText)) {
        handleNudgeAdvisor(queryText);
        return;
      }
      if (queryText === IMPROVE_QUICK_QUESTION || isImproveConversionRequest(queryText)) {
        handleImprove(queryText);
        return;
      }

      const userMsg = {
        id: `user_${Date.now()}`,
        sender: "user",
        text: queryText,
        timestamp: new Date().toLocaleTimeString([], {
          hour: "2-digit",
          minute: "2-digit",
        }),
      };

      const agentMsgId = `agent_${Date.now()}`;
      const agentMsg = {
        id: agentMsgId,
        sender: "agent",
        text: "",
        isStreaming: true,
        progressStep: 0,
        progressMessage: "Initializing AI...",
        timestamp: new Date().toLocaleTimeString([], {
          hour: "2-digit",
          minute: "2-digit",
        }),
      };

      setChatMessages((prev) => [...prev, userMsg, agentMsg]);
      setIsTyping(true);

      let accumulatedText = "";

      try {
        const streamUrl = agentAPI.streamAsk();
        await consumeSSEStream(streamUrl, {
          method: "POST",
          body: {
            collectionName: clientId,
            question: queryText,
            currentPage: location.pathname,
          },
          onProgress: (step, message) => {
            setChatMessages((prev) =>
              prev.map((m) =>
                m.id === agentMsgId
                  ? {
                      ...m,
                      progressStep: step,
                      progressMessage: message,
                    }
                  : m
              )
            );
          },
          onResult: (data) => {
            accumulatedText = data.answer || "No reply received.";
            const followUps = data.followUpQuestions || [];
            if (data.hasContext) {
              setHasContext(true);
            }
            setChatMessages((prev) =>
              prev.map((m) =>
                m.id === agentMsgId
                  ? {
                      ...m,
                      text: accumulatedText,
                      followUpQuestions: followUps,
                      isStreaming: false,
                    }
                  : m
              )
            );
            setIsTyping(false);
          },
          onError: (errorMsg) => {
            setChatMessages((prev) =>
              prev.map((m) =>
                m.id === agentMsgId
                  ? {
                      ...m,
                      text: `Error: ${errorMsg}`,
                      isStreaming: false,
                    }
                  : m
              )
            );
            setIsTyping(false);
          },
        });

        // Refresh recommendations after a response
        dispatch(fetchRecommendations(clientId));
      } catch (err) {
        console.error("Drawer chat error:", err);
        setChatMessages((prev) =>
          prev.map((m) =>
            m.id === agentMsgId
              ? {
                  ...m,
                  text: `Failed to communicate with agent. ${err.message || ""}`,
                  isStreaming: false,
                }
              : m
          )
        );
        setIsTyping(false);
      }
    },
    [inputVal, clientId, isTyping, location.pathname, dispatch]
  );

  const handleClearHistory = () => {
    setChatMessages([]);
    setHasContext(false);
    dispatch(clearChatHistory());
    agentAPI.clearConversation(clientId);
  };

  // Open the AI Insights as a full page (like other dashboard pages)
  const goFullPage = () => {
    if (onClose) onClose();
    navigate("/ai-insights");
  };

  // Exit full page back to the dashboard with the drawer open
  const exitFullPage = () => {
    navigate("/");
    dispatch(setDrawerOpen(true));
  };

  // In page mode the close/back buttons navigate home instead of closing an overlay
  const handleClose = asPage ? () => navigate("/") : onClose;

  const SUGGESTED_QUESTIONS = [
    IMPROVE_QUICK_QUESTION,
    NUDGE_QUICK_QUESTION,
    ABTEST_QUICK_QUESTION,
    LANDING_QUICK_QUESTION,
    "What is my biggest conversion problem?",
    "Which pages need the most attention?",
    "How are my nudges performing?",
  ];

  if (!clientId) return null;

  // Shared body (header + tabs + content) — identical in drawer and full page mode
  const panelBody = (
    <div className="w-full h-full flex flex-col overflow-hidden">
          {/* ─── Header ─── */}
          <div className="shrink-0 px-5 pt-5 pb-0">
            {/* Title bar */}
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2.5">
                <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-cyan-500 to-indigo-600 flex items-center justify-center shadow-lg shadow-cyan-500/25">
                  <HiSparkles className="text-white text-lg" />
                </div>
                <div>
                  <h2 className="text-base font-bold text-cyan-600 dark:text-cyan-500 leading-tight">
                    AI Insights
                  </h2>
                  <p className="text-[10px] text-slate-400 font-medium tracking-wide uppercase">
                    Powered by CausalFunnel AI
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-1">
                {activeTab === "insights" && (
                  <button
                    onClick={() => {
                      dispatch({ type: "agent/fetchRecommendations/pending" });
                      loadRecommendations();
                    }}
                    disabled={requestInProgress || loading}
                    className="text-xs text-slate-400 hover:text-slate-600 flex items-center gap-1 px-2.5 py-1.5 rounded-lg hover:bg-slate-100 transition-all duration-200"
                    title="Refresh insights"
                  >
                    🔄 Refresh
                  </button>
                )}
                {activeTab === "ask" && chatMessages.length > 0 && (
                  <button
                    onClick={handleClearHistory}
                    className="w-8 h-8 rounded-lg flex items-center justify-center text-slate-400 hover:text-rose-600 hover:bg-rose-50 transition-all duration-200"
                    title="Clear Chat"
                  >
                    <FaTrashAlt className="text-xs" />
                  </button>
                )}
                {/* Desktop full page / exit full page toggle */}
                <button
                  onClick={asPage ? exitFullPage : goFullPage}
                  className="hidden sm:flex w-8 h-8 rounded-lg items-center justify-center text-slate-400 hover:text-cyan-600 hover:bg-slate-100 transition-all duration-200"
                  title={asPage ? "Exit full page" : "Open full page"}
                  aria-label={asPage ? "Exit full page" : "Open full page"}
                >
                  {asPage ? (
                    <FaCompress className="text-sm" />
                  ) : (
                    <FaExpand className="text-sm" />
                  )}
                </button>
                {/* Mobile Back Arrow Button */}
                <button
                  onClick={handleClose}
                  className="sm:hidden w-8 h-8 rounded-lg flex items-center justify-center text-slate-500 hover:bg-slate-100 text-lg font-bold"
                >
                  ←
                </button>
                {/* Desktop close button */}
                <button
                  onClick={handleClose}
                  className="hidden sm:flex w-8 h-8 rounded-lg items-center justify-center text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-all duration-200"
                >
                  <FaTimes className="text-sm" />
                </button>
              </div>
            </div>

            {/* Tab switcher */}
            <div className="flex bg-slate-100 rounded-xl p-1 gap-1">
              <button
                onClick={() => setActiveTab("insights")}
                className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold transition-all duration-300 ${
                  activeTab === "insights"
                    ? "bg-cyan-500 text-white shadow-sm"
                    : "text-slate-500 hover:text-slate-700"
                }`}
              >
                <FaRegLightbulb
                  className={
                    activeTab === "insights"
                      ? "text-white"
                      : "text-slate-400"
                  }
                />
                Insights
                {recommendations.length > 0 && (
                  <span
                    className={`ml-1 px-1.5 py-0.5 rounded-full text-[10px] font-bold ${
                      activeTab === "insights"
                        ? "bg-white/20 text-white"
                        : "bg-slate-200 text-slate-500"
                    }`}
                  >
                    {recommendations.length}
                  </span>
                )}
              </button>
              <button
                onClick={() => setActiveTab("ask")}
                className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold transition-all duration-300 ${
                  activeTab === "ask"
                    ? "bg-cyan-500 text-white shadow-sm"
                    : "text-slate-500 hover:text-slate-700"
                }`}
              >
                <FaCommentDots
                  className={
                    activeTab === "ask" ? "text-white" : "text-slate-400"
                  }
                />
                Ask AI
              </button>
            </div>

            {/* Separator */}
            <div className="h-px bg-gradient-to-r from-transparent via-slate-200 to-transparent mt-4" />
          </div>

          {/* ─── Tab Content ─── */}
          {activeTab === "insights" ? (
            /* ─── INSIGHTS TAB ─── */
            <div className="flex-grow overflow-y-auto px-5 py-4 space-y-4 scrollbar-thin">
              {/* Data Summary (Starts Collapsed by default) */}
              {dataSummary && (
                <div className="bg-gradient-to-r from-cyan-50/80 to-blue-50/80 rounded-xl border border-cyan-100/60 text-xs overflow-hidden transition-all duration-300">
                  <button
                    onClick={() => setIsSummaryExpanded(!isSummaryExpanded)}
                    className="w-full flex items-center justify-between p-3.5 text-left font-bold text-slate-500 uppercase tracking-wider text-[10px] hover:bg-cyan-100/30 transition-colors duration-200 focus:outline-none"
                  >
                    <span>Data Summary</span>
                    {isSummaryExpanded ? (
                      <FaChevronUp className="text-xs text-slate-400" />
                    ) : (
                      <FaChevronDown className="text-xs text-slate-400" />
                    )}
                  </button>
                  <div
                    className={`transition-all duration-300 ease-in-out overflow-hidden ${
                      isSummaryExpanded ? "max-h-[500px] opacity-100 px-3.5 pb-3.5" : "max-h-0 opacity-0 pointer-events-none"
                    }`}
                  >
                    <p className="text-slate-700 leading-relaxed font-medium">
                      {safeRender(dataSummary)}
                    </p>
                  </div>
                </div>
              )}

              {/* Progress Steps / Loader */}
              {(loading || progressStep) && recommendations.length === 0 ? (
                progressStep !== null ? (
                  <div className="bg-slate-50 p-4 rounded-xl border border-slate-200/60">
                    <ProgressSteps currentStep={progressStep} currentMessage={progressMessage} />
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center py-16 text-slate-400">
                    <FaSpinner className="animate-spin text-2xl mb-3 text-cyan-500" />
                    <span className="text-xs font-semibold">
                      Analyzing your data...
                    </span>
                  </div>
                )
              ) : error ? (
                <div className="p-4 text-center text-xs text-rose-600 bg-rose-50 border border-rose-100 rounded-xl space-y-2">
                  <p>{safeRender(error)}</p>
                  <button
                    onClick={loadRecommendations}
                    className="px-3.5 py-1.5 bg-slate-900 text-white rounded-lg font-semibold hover:bg-slate-800 transition-colors text-[11px]"
                  >
                    Try Again
                  </button>
                </div>
              ) : (recommendations.length === 0 && !loading && !progressStep) ? (
                <div className="p-10 text-center text-slate-400 bg-slate-50/50 border border-dashed border-slate-200 rounded-xl">
                  <span className="text-3xl mb-2 block">🎉</span>
                  <p className="text-sm font-semibold mb-1 text-slate-600">
                    Looking good!
                  </p>
                  <p className="text-xs">
                    No critical issues found right now.
                  </p>
                </div>
              ) : (
                recommendations.map((rec, index) => (
                  <div
                    key={rec.id}
                    className="animate-in fade-in slide-in-from-bottom-3 duration-300"
                    style={{ animationDelay: `${index * 80}ms` }}
                  >
                    <RecommendationCard
                      recommendation={rec}
                      clientId={clientId}
                    />
                  </div>
                ))
              )}
            </div>
          ) : (
            /* ─── ASK AI TAB ─── */
            <div className="flex-grow flex flex-col overflow-hidden">
              {/* Context Memory Indicator */}
              {hasContext && chatMessages.length > 0 && (
                <div className="px-5 py-1.5 bg-slate-100 border-b border-slate-200/60 flex items-center gap-1 shrink-0">
                  <span className="text-[10px] text-slate-500 font-medium">
                    🧠 Remembers previous messages
                  </span>
                </div>
              )}

              {/* Chat messages */}
              <div className="flex-grow overflow-y-auto px-5 py-4 space-y-4 scrollbar-thin">
                {chatMessages.length === 0 ? (
                  <div className="flex flex-col items-center justify-center h-full text-center py-8">
                    <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-cyan-500 to-indigo-600 flex items-center justify-center mb-4 shadow-lg shadow-cyan-500/20">
                      <FaRobot className="text-2xl text-white" />
                    </div>
                    <h5 className="font-bold text-slate-700 text-sm mb-2">
                      Ask me anything about your site
                    </h5>
                    <p className="text-xs text-slate-400 max-w-[240px] leading-relaxed mb-4">
                      I can analyze your traffic, conversions, A/B tests, nudges, heatmaps, and more.
                    </p>
                    {/* Suggested Questions */}
                    <div className="space-y-2 w-full max-w-[280px] text-left">
                      <p className="text-slate-500 text-xs font-semibold px-1">Try asking:</p>
                      {SUGGESTED_QUESTIONS.map((prompt) => (
                        <button
                          key={prompt}
                          onClick={(e) => handleSend(e, prompt)}
                          className="w-full text-left px-3.5 py-2.5 rounded-xl border border-slate-200 bg-white hover:border-cyan-300 hover:bg-cyan-50/30 text-xs text-slate-600 hover:text-cyan-700 transition-all duration-200 font-medium"
                        >
                          {prompt}
                        </button>
                      ))}
                    </div>
                  </div>
                ) : (
                  chatMessages.map((msg) => {
                    const isAgent = msg.sender === "agent";
                    // A generated UI artifact rendered as a chat message (file card).
                    if (msg.artifactCard) {
                      return (
                        <div key={msg.id} className="flex items-start gap-2.5">
                          <div className="w-7 h-7 rounded-full shrink-0 flex items-center justify-center text-xs font-semibold bg-gradient-to-br from-cyan-500 to-indigo-600 text-white">
                            <FaRobot />
                          </div>
                          <div className="flex flex-col w-full max-w-[85%] items-start">
                            <ArtifactCard
                              artifact={msg.artifactCard}
                              onOpen={() => { if (onClose) onClose(); navigate("/ab-test-studio"); }}
                            />
                            <span className="text-[9px] text-slate-400 mt-1 px-1">{msg.timestamp}</span>
                          </div>
                        </div>
                      );
                    }
                    return (
                      <div
                        key={msg.id}
                        className={`flex items-start gap-2.5 ${
                          isAgent ? "" : "flex-row-reverse"
                        }`}
                      >
                        <div
                          className={`w-7 h-7 rounded-full shrink-0 flex items-center justify-center text-xs font-semibold ${
                            isAgent
                              ? "bg-gradient-to-br from-cyan-500 to-indigo-600 text-white"
                              : "bg-slate-800 text-white"
                          }`}
                        >
                          {isAgent ? <FaRobot /> : <FaUser />}
                        </div>
                        <div
                          className={`flex flex-col max-w-[80%] ${
                            isAgent ? "items-start" : "items-end"
                          }`}
                        >
                          <div
                            className={`p-3 rounded-2xl text-xs leading-relaxed shadow-sm ${
                              isAgent
                                ? "bg-white border border-slate-200/80 text-slate-700 rounded-tl-none"
                                : "bg-gradient-to-r from-cyan-600 to-indigo-600 text-white rounded-tr-none"
                            }`}
                          >
                            {msg.isStreaming ? (
                              <div className="space-y-2">
                                <ProgressSteps
                                  currentStep={msg.progressStep}
                                  currentMessage={msg.progressMessage}
                                />
                                {queueText && (
                                  <div className="text-[10px] text-slate-500 font-semibold bg-slate-50 p-2 rounded border border-slate-200/50 flex items-center gap-1.5 animate-pulse">
                                    <span>⏳</span> {queueText}
                                  </div>
                                )}
                              </div>
                            ) : msg.text ? (
                              isAgent ? (
                                <ChatMarkdown text={safeRender(msg.text)} />
                              ) : (
                                <p style={{ whiteSpace: "pre-wrap" }}>{safeRender(msg.text)}</p>
                              )
                            ) : (
                              <div className="flex items-center gap-1.5 py-0.5">
                                <span className="w-1.5 h-1.5 bg-cyan-500 rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
                                <span className="w-1.5 h-1.5 bg-cyan-500 rounded-full animate-bounce" style={{ animationDelay: "150ms" }} />
                                <span className="w-1.5 h-1.5 bg-cyan-500 rounded-full animate-bounce" style={{ animationDelay: "300ms" }} />
                              </div>
                            )}
                          </div>

                          {/* Studio page candidates (read-only recap in the drawer) */}
                          {isAgent && Array.isArray(msg.candidates) && msg.candidates.length > 0 && (
                            <div className="mt-2 w-full space-y-1">
                              {msg.candidates.map((c, i) => (
                                <div key={i} className="px-3 py-2 rounded-lg border border-slate-200 bg-white">
                                  <span className="block text-[11px] font-semibold text-slate-700">{c.page}</span>
                                  <span className="block text-[10px] text-slate-500">{c.reason}</span>
                                </div>
                              ))}
                            </div>
                          )}

                          {/* AI nudge draft card */}
                          {isAgent && msg.nudgeSpec && (
                            <div className="mt-2 w-full rounded-xl border border-cyan-200 bg-cyan-50/60 p-3 text-[11px] text-slate-700 shadow-sm">
                              <div className="flex items-center gap-1.5 font-semibold text-cyan-700 mb-1.5">
                                <FaBullhorn className="text-[10px]" /> Suggested nudge
                              </div>
                              <ul className="space-y-1 mb-2">
                                <li><span className="text-slate-400">Target:</span> {safeRender(msg.nudgeSpec.nudgeType === "returning" ? "Returning visitors" : "First-time visitors")} ({safeRender(msg.nudgeSpec.targetAudience)})</li>
                                <li><span className="text-slate-400">Trigger:</span> {safeRender((msg.nudgeSpec.condition || "").replace(/_/g, " "))} on {safeRender((msg.nudgeSpec.allowedPages || ["/"]).join(", "))}</li>
                                <li><span className="text-slate-400">Offer:</span> “{safeRender(msg.nudgeSpec.offerText)}”{msg.nudgeSpec.couponCode ? ` · ${safeRender(msg.nudgeSpec.couponCode)}` : ""}</li>
                                {msg.nudgeSpec.reasoning && (
                                  <li className="text-slate-500 italic">{safeRender(msg.nudgeSpec.reasoning)}</li>
                                )}
                              </ul>
                              {msg.draftCreated ? (
                                <div className="flex items-center gap-1.5 text-emerald-600 font-semibold">
                                  <FaCheck className="text-[10px]" /> Draft created — review & publish in the editor
                                </div>
                              ) : (
                                <button
                                  type="button"
                                  disabled={draftingId === msg.id}
                                  onClick={() => handleCreateDraft(msg.id, msg.nudgeSpec)}
                                  className="w-full mt-0.5 px-3 py-2 rounded-lg bg-slate-900 hover:bg-slate-800 text-white text-[11px] font-semibold shadow-sm disabled:opacity-50 transition-all flex items-center justify-center gap-1.5"
                                >
                                  {draftingId === msg.id ? (
                                    <><FaSpinner className="animate-spin text-[10px]" /> Creating draft…</>
                                  ) : (
                                    <>Create draft &amp; review</>
                                  )}
                                </button>
                              )}
                            </div>
                          )}

                          {/* Improve-conversion action cards */}
                          {isAgent && msg.improveActions && (
                            <div className="mt-2 w-full space-y-1.5">
                              {["nudge", "abtest", "landing"].map((k) => {
                                const a = msg.improveActions[k];
                                if (!a) return null;
                                return (
                                  <button
                                    key={k}
                                    type="button"
                                    onClick={() => handleImproveAction(a.action || k)}
                                    className="w-full text-left px-3 py-2 rounded-lg border border-slate-200 bg-white hover:border-cyan-300 hover:bg-cyan-50/40 transition-colors"
                                  >
                                    <span className="block text-xs font-semibold text-slate-800">{safeRender(a.label)}</span>
                                    <span className="block text-[11px] text-slate-500">{safeRender(a.reason)}</span>
                                  </button>
                                );
                              })}
                            </div>
                          )}

                          {/* Follow up questions block */}
                          {isAgent && msg.followUpQuestions && msg.followUpQuestions.length > 0 && (
                            <div className="mt-2 flex flex-wrap gap-1.5 w-full">
                              {msg.followUpQuestions.map((q, idx) => (
                                <button
                                  key={idx}
                                  type="button"
                                  onClick={(e) => handleSend(e, typeof q === "string" ? q : q?.text || q?.question)}
                                  className="text-left px-3 py-1.5 rounded-full border border-slate-200 hover:border-cyan-300 hover:bg-cyan-50/40 text-[10px] text-slate-600 hover:text-cyan-700 transition-all duration-200 font-semibold bg-white/85 shadow-sm"
                                >
                                  {typeof q === "string" ? q : q?.text || q?.question || "Suggested Action"}
                                </button>
                              ))}
                            </div>
                          )}

                          <span className="text-[9px] text-slate-400 mt-1 px-1">
                            {msg.timestamp}
                          </span>
                        </div>
                      </div>
                    );
                  })
                )}
                <div ref={messagesEndRef} />
              </div>

              {/* Input bar */}
              <div className="shrink-0 px-5 py-3 border-t border-slate-200/80 bg-white/60">
                <form onSubmit={handleSend} className="flex items-center gap-2">
                  <input
                    ref={inputRef}
                    type="text"
                    value={inputVal}
                    onChange={(e) => setInputVal(e.target.value)}
                    placeholder={isTyping ? "Still thinking..." : "Ask about your site data..."}
                    disabled={isTyping}
                    className="flex-grow px-4 py-2.5 bg-slate-100 border-0 outline-none rounded-xl text-xs text-slate-800 placeholder-slate-400 disabled:opacity-60 focus:ring-2 focus:ring-cyan-500/30 focus:bg-white transition-all duration-200"
                  />
                  <button
                    type="submit"
                    disabled={!inputVal.trim() || isTyping}
                    className="px-4 py-2.5 flex items-center justify-center bg-gradient-to-r from-cyan-600 to-indigo-600 hover:from-cyan-500 hover:to-indigo-500 text-white rounded-xl shadow-sm hover:shadow-md disabled:opacity-40 disabled:hover:shadow-sm transition-all duration-200 shrink-0 min-w-[80px]"
                  >
                    {isTyping ? (
                      <div className="flex items-center gap-1.5">
                        <FaSpinner className="animate-spin text-xs" />
                        <span className="text-[10px] font-semibold">Thinking...</span>
                      </div>
                    ) : (
                      <div className="flex items-center gap-1.5">
                        <FaPaperPlane className="text-[11px]" />
                        <span className="text-[10px] font-semibold">Send</span>
                      </div>
                    )}
                  </button>
                </form>
              </div>
            </div>
          )}
    </div>
  );

  // ─── Full page mode: render inline like a normal dashboard page ───
  if (asPage) {
    return (
      <div
        className="h-full w-full flex flex-col"
        style={{
          background:
            "linear-gradient(135deg, rgba(255,255,255,0.96) 0%, rgba(248,250,252,0.98) 100%)",
        }}
      >
        <div className="w-full max-w-4xl mx-auto h-full flex flex-col overflow-hidden">
          {panelBody}
        </div>
      </div>
    );
  }

  // ─── Drawer mode: slide-in overlay panel ───
  return (
    <>
      {/* Backdrop overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 z-[60] bg-black/25 backdrop-blur-[2px] transition-all duration-300"
          onClick={onClose}
          aria-hidden="true"
        />
      )}

      {/* Drawer panel - Responsive sizes */}
      <div
        className={`fixed top-0 right-0 h-full z-[61] flex transition-transform duration-300 pointer-events-auto w-full sm:w-[380px] lg:w-[420px]`}
        style={{
          transform: isOpen ? "translateX(0)" : "translateX(100%)",
          transitionTimingFunction: "cubic-bezier(0.32, 0.72, 0, 1)",
          background:
            "linear-gradient(135deg, rgba(255,255,255,0.96) 0%, rgba(248,250,252,0.98) 100%)",
          backdropFilter: "blur(24px) saturate(180%)",
          WebkitBackdropFilter: "blur(24px) saturate(180%)",
        }}
      >
        {/* Left glow edge */}
        <div className="w-[1px] h-full bg-gradient-to-b from-cyan-400/40 via-purple-400/20 to-cyan-400/40 shrink-0" />
        {panelBody}
      </div>
    </>
  );
};

export { AIDrawer };
export default AIDrawer;
